import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useWallet } from "@/context/WalletContext";
import { MoonMark } from "@/components/MoonMark";
import { VENUE_META, logoUrl, DEST_META } from "@/lib/meta";
import {
  Wallet, ShieldCheck, ChevronRight, ChevronLeft, Check, Flame, Loader2, Twitter, Send, MessageCircle,
  UploadCloud, BadgeCheck, Sparkles, RefreshCw,
} from "lucide-react";

const STEPS = ["Wallet", "Qualify", "Venue", "Token", "Socials", "Strategy", "Review & pay"];

const StepDots = ({ step }) => (
  <div className="mb-8 flex items-center gap-1.5" data-testid="wizard-steps">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-1.5">
        <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium transition-colors ${
          i < step ? "bg-pulp text-ink" : i === step ? "bg-juice text-ink" : "bg-raised text-dust"
        }`}>
          {i < step ? <Check size={12} /> : i + 1}
        </div>
        {i < STEPS.length - 1 && <div className={`h-px w-4 ${i < step ? "bg-pulp" : "bg-line"}`} />}
      </div>
    ))}
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-dust">{label}</Label>
    {children}
    {hint && <p className="text-xs text-dust/70">{hint}</p>}
  </div>
);

export default function Wizard() {
  const navigate = useNavigate();
  const { wallet, user, refreshUser } = useWallet();
  const [step, setStep] = useState(0);
  const [venues, setVenues] = useState([]);
  const [packs, setPacks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [styleHint, setStyleHint] = useState("");
  const [logoOptions, setLogoOptions] = useState([]);
  const [regenIdx, setRegenIdx] = useState(null);
  const [urlMode, setUrlMode] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [destCard, setDestCard] = useState(null);

  const [form, setForm] = useState({
    creator_type: "creator", token_type: "meme", controls_socials: true,
    no_guarantee: false, no_impersonation: false,
    venue: "", name: "", ticker: "", description: "", image_url: "",
    venue_fields: {}, socials: { x: "", telegram: "", discord: "" },
    goal: "awareness", duration_days: 7, voice: "degen", pack: "",
    logo: null, payout_destination: { type: "wallet", ref: "", verified: false, split_pct: 0 },
  });

  useEffect(() => {
    api.get("/venues").then(({ data }) => setVenues(data)).catch(() => {});
    api.get("/billing/packs").then(({ data }) => setPacks(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVF = (k, v) => setForm((f) => ({ ...f, venue_fields: { ...f.venue_fields, [k]: v } }));
  const setSoc = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));
  const setDest = (patch) => setForm((f) => ({ ...f, payout_destination: { ...f.payout_destination, ...patch } }));

  const onUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/logo/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo", data.logo);
      toast.success("Logo pinned to IPFS. Ready.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const useUrl = () => {
    if (!form.image_url.trim()) return;
    set("logo", { status: "ready", cdn_512: form.image_url.trim(), ipfs: "ipfs://(external)", note: "external" });
    toast.success("Using pasted URL.");
  };

  const genLogo = async () => {
    if (!form.name.trim() || !form.ticker.trim()) { toast.error("Add a name and ticker first."); return; }
    setGenLoading(true);
    setLogoOptions([]);
    try {
      const { data } = await api.post("/logo/generate-variations", { name: form.name, ticker: form.ticker, description: form.description, style_hint: styleHint, count: 4 });
      setLogoOptions(data.options || []);
      if (data.options?.length) { set("logo", data.options[0].logo); toast.success(`${data.options.length} logo options ready — pick your favourite.`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); }
    finally { setGenLoading(false); }
  };

  const regenOne = async (i, e) => {
    e.stopPropagation();
    const opt = logoOptions[i];
    setRegenIdx(i);
    try {
      const { data } = await api.post("/logo/generate", { name: form.name, ticker: form.ticker, description: form.description, style_hint: [styleHint, opt?.logo?.variation].filter(Boolean).join(", ") });
      const wasSelected = form.logo?.cdn_512 === opt?.logo?.cdn_512;
      setLogoOptions((arr) => arr.map((x, idx) => (idx === i ? { ...x, logo: data.logo } : x)));
      if (wasSelected) set("logo", data.logo);
      toast.success(`Option ${i + 1} re-rolled.`);
    } catch (err) { toast.error("Re-roll failed."); }
    finally { setRegenIdx(null); }
  };

  const resolveDest = async () => {
    const { type, ref } = form.payout_destination;
    if (type === "wallet" || !ref.trim()) return;
    setResolving(true);
    try {
      const { data } = await api.post("/resolve-handle", { type, ref });
      setDestCard(data);
      setDest({ verified: data.verified });
    } catch (e) { toast.error("Could not resolve handle."); }
    finally { setResolving(false); }
  };

  const currentVenue = venues.find((v) => v.id === form.venue);
  const currentPack = packs.find((p) => p.id === form.pack);

  const logoReady = form.logo?.status === "ready";
  const destValid = form.payout_destination.type === "wallet" || form.payout_destination.ref.trim().length >= 2;

  const canNext = () => {
    if (step === 1) return form.no_guarantee && form.no_impersonation;
    if (step === 2) return !!form.venue;
    if (step === 3) return form.name.trim() && form.ticker.trim() && logoReady;
    if (step === 5) return destValid;
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/launches", {
        ...form,
        ticker: form.ticker.replace("$", ""),
        payout_destination: {
          type: form.payout_destination.type,
          ref: form.payout_destination.type === "wallet" ? "" : form.payout_destination.ref.trim(),
          verified: form.payout_destination.verified,
          split_pct: form.payout_destination.split_pct ?? (form.payout_destination.type === "wallet" ? 0 : 100),
        },
      });
      await refreshUser();
      toast.success(`$${data.ticker} is live. The engine is running.`);
      navigate(`/app/launches/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the launch. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-8 lg:px-8">
      <h1 className="text-2xl font-bold tracking-tight">New launch</h1>
      <p className="mb-6 text-sm text-dust">Wallet in. Pick a pad. Launch free. Agents post the plan you approve.</p>
      <StepDots step={step} />

      <div className="rounded-2xl border border-line bg-surface p-6" data-testid={`wizard-step-${step}`}>
        {/* 0 Wallet */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-raised text-juice"><Wallet size={20} /></div>
              <div><h2 className="text-lg font-medium">Your wallet signs the launch</h2><p className="text-sm text-dust">Wen Moon never holds the mint key.</p></div>
            </div>
            <div className="rounded-xl border border-line bg-raised/50 p-4">
              <p className="text-xs uppercase tracking-wider text-dust">Connected</p>
              <p className="mt-1 flex items-center gap-2 font-mono text-sm text-pulp"><span className="h-2 w-2 rounded-full bg-pulp animate-soft-pulse" />{wallet}</p>
              {(user?.juice_balance ?? 0) > 0 && <p className="mt-1 font-mono text-xs text-dust">Boost fuel on hand: ${user.juice_balance}</p>}
            </div>
            <div className="rounded-xl border border-pulp/25 bg-pulp/5 p-3 text-xs leading-relaxed text-dust">
              Launch is free. You keep <span className="text-cream">70%</span> of pad creator fees. <span className="text-cream">15%</span> runs the protocol. <span className="text-pulp">15%</span> buys and burns $MOON.
            </div>
            <div className="flex items-center gap-2 text-xs text-dust"><ShieldCheck size={14} className="text-pulp" /> Non-custodial. You approve every transaction.</div>
          </div>
        )}

        {/* 1 Qualify */}
        {step === 1 && (
          <div className="space-y-5">
            <h2 className="text-lg font-medium">Quick qualify</h2>
            <Field label="Are you the creator or a client launching for someone?">
              <div className="grid grid-cols-2 gap-2">
                {["creator", "client"].map((t) => (
                  <button key={t} onClick={() => set("creator_type", t)} data-testid={`qualify-type-${t}`}
                    className={`rounded-lg border px-4 py-2.5 text-sm capitalize transition-colors ${form.creator_type === t ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>{t}</button>
                ))}
              </div>
            </Field>
            <Field label="Do you control the social accounts?">
              <div className="grid grid-cols-2 gap-2">
                {[true, false].map((t) => (
                  <button key={String(t)} onClick={() => set("controls_socials", t)}
                    className={`rounded-lg border px-4 py-2.5 text-sm transition-colors ${form.controls_socials === t ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>{t ? "Yes" : "Not yet"}</button>
                ))}
              </div>
            </Field>
            <div className="space-y-3 rounded-xl border border-line bg-raised/40 p-4">
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={form.no_guarantee} onCheckedChange={(v) => set("no_guarantee", !!v)} data-testid="check-no-guarantee" className="mt-0.5" />
                <span className="text-dust">I won't ask agents to guarantee price, volume, or that the token will trend.</span>
              </label>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={form.no_impersonation} onCheckedChange={(v) => set("no_impersonation", !!v)} data-testid="check-no-impersonation" className="mt-0.5" />
                <span className="text-dust">I won't impersonate real people or brands.</span>
              </label>
            </div>
            {!canNext() && <p className="text-xs text-warn">Tick both boxes to continue.</p>}
          </div>
        )}

        {/* 2 Venue */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Pick a pad</h2>
            <p className="text-sm text-dust">Wen Moon is not a launchpad. It launches on the pad you pick.</p>
            <div className="grid grid-cols-2 gap-3">
              {venues.map((v) => {
                const selected = form.venue === v.id;
                return (
                  <button key={v.id} onClick={() => set("venue", v.id)} data-testid={`venue-select-${v.id}`}
                    className={`rounded-xl border p-4 text-left transition-colors ${selected ? "border-juice bg-juice/10" : "border-line bg-raised/30 hover:bg-raised"}`}>
                    <div className="mb-2" style={{ color: VENUE_META[v.id]?.color }}><MoonMark size={22} /></div>
                    <p className="font-medium text-cream">{v.name}</p>
                    <p className="font-mono text-xs text-dust">{v.chain}</p>
                    <p className="mt-2 text-xs leading-snug text-dust/80">{v.blurb}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 3 Token */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Token details</h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Fox Money" className="bg-raised border-line" data-testid="token-name" /></Field>
              <Field label="Ticker"><Input value={form.ticker} onChange={(e) => set("ticker", e.target.value.toUpperCase())} placeholder="FOX" className="bg-raised border-line font-mono" data-testid="token-ticker" /></Field>
            </div>
            <Field label="Description"><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="One dry line about the token." className="bg-raised border-line" data-testid="token-description" /></Field>
            <Field label="Token logo" hint="PNG, JPG, WEBP or GIF · max 8 MB · auto center-cropped to square. Pinned to IPFS.">
              <div className="flex items-start gap-4">
                <label
                  htmlFor="logo-input"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files); }}
                  className="flex h-28 flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-raised/40 text-center transition-colors hover:border-juice/50"
                  data-testid="logo-dropzone"
                >
                  {uploading ? (
                    <><Loader2 size={20} className="animate-spin text-juice" /><span className="text-xs text-dust">Processing & pinning…</span></>
                  ) : logoReady ? (
                    <><BadgeCheck size={20} className="text-pulp" /><span className="text-xs text-pulp">Logo ready</span><span className="font-mono text-[10px] text-dust">{form.logo.ipfs?.slice(0, 22)}…</span></>
                  ) : (
                    <><UploadCloud size={20} className="text-dust" /><span className="text-xs text-dust">Drop a logo or click to upload</span></>
                  )}
                  <input id="logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid="logo-file-input" />
                </label>
                {logoReady && (
                  <img src={logoUrl(form.logo)} alt="logo preview" className="h-28 w-28 rounded-xl border border-line object-cover" data-testid="logo-preview" />
                )}
              </div>
              <div className="mt-3 rounded-xl border border-ai/30 bg-ai/5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-ai"><Sparkles size={14} /> AI logo maker</span>
                  <Button type="button" onClick={genLogo} disabled={genLoading} size="sm" className="gap-1.5 bg-ai text-ink hover:bg-ai/90 font-semibold" data-testid="logo-generate">
                    {genLoading ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : (logoOptions.length ? "Regenerate options" : "Generate options")}
                  </Button>
                </div>
                <Input value={styleHint} onChange={(e) => setStyleHint(e.target.value)} placeholder="Optional style hint (e.g. neon, retro, mascot)" className="mt-2 bg-surface border-line text-sm" data-testid="logo-style-hint" />
                <p className="mt-1.5 text-[11px] text-dust">Auto-uses your name, ticker and description. Get 4 options and pick one, or add a hint and regenerate.</p>
                {(genLoading || logoOptions.length > 0) && (
                  <div className="mt-3 grid grid-cols-4 gap-2" data-testid="logo-options">
                    {genLoading && logoOptions.length === 0
                      ? [0, 1, 2, 3].map((i) => <div key={i} className="aspect-square animate-pulse rounded-lg border border-line bg-raised/60" />)
                      : logoOptions.map((o, i) => {
                          const selected = form.logo?.cdn_512 === o.logo.cdn_512;
                          return (
                            <button key={i} type="button" onClick={() => set("logo", o.logo)} data-testid={`logo-option-${i}`}
                              className={`group relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${selected ? "border-juice ring-2 ring-juice/40" : "border-line hover:border-ai/60"}`}>
                              <img src={logoUrl(o.logo)} alt={`option ${i + 1}`} className="h-full w-full object-cover" />
                              {selected && <span className="absolute right-1 top-1 rounded-full bg-juice p-0.5 text-ink"><Check size={11} /></span>}
                              <span onClick={(e) => regenOne(i, e)} data-testid={`logo-reroll-${i}`}
                                className="absolute left-1 top-1 rounded-full bg-ink/70 p-1 text-cream opacity-0 transition-opacity hover:bg-ai group-hover:opacity-100" title="Re-roll this option">
                                {regenIdx === i ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                              </span>
                            </button>
                          );
                        })}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => setUrlMode((v) => !v)} className="mt-2 text-xs text-dust underline-offset-2 hover:text-cream hover:underline" data-testid="logo-url-toggle">
                {urlMode ? "Hide" : "Or paste an image URL"}
              </button>
              {urlMode && (
                <div className="mt-2 flex gap-2">
                  <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" className="bg-raised border-line" data-testid="logo-url-input" />
                  <Button type="button" onClick={useUrl} variant="outline" className="border-line bg-raised hover:bg-line/40" data-testid="logo-url-use">Use</Button>
                </div>
              )}
            </Field>
            {currentVenue?.fields?.map((f) => (
              <Field key={f.key} label={`${currentVenue.name} · ${f.label}`}>
                <Input type={f.type} value={form.venue_fields[f.key] || ""} onChange={(e) => setVF(f.key, e.target.value)} placeholder={f.placeholder} className="bg-raised border-line font-mono" data-testid={`venue-field-${f.key}`} />
              </Field>
            ))}
          </div>
        )}

        {/* 4 Socials */}
        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Connect socials</h2>
            <p className="text-sm text-dust">X, Telegram and Discord are live. IG / FB / TikTok / Reddit ship as a pack.</p>
            {[
              { k: "x", label: "X (Twitter)", icon: Twitter, ph: "@handle" },
              { k: "telegram", label: "Telegram", icon: Send, ph: "t.me/group" },
              { k: "discord", label: "Discord", icon: MessageCircle, ph: "discord.gg/invite" },
            ].map(({ k, label, icon: Icon, ph }) => (
              <div key={k} className="flex items-center gap-3 rounded-xl border border-line bg-raised/40 p-3">
                <Icon size={18} className="text-ai" />
                <span className="w-28 text-sm text-cream">{label}</span>
                <Input value={form.socials[k]} onChange={(e) => setSoc(k, e.target.value)} placeholder={ph} className="flex-1 bg-surface border-line" data-testid={`social-${k}`} />
              </div>
            ))}
          </div>
        )}

        {/* 5 Strategy */}
        {step === 5 && (
          <div className="space-y-5">
            <h2 className="text-lg font-medium">Strategy & boost</h2>
            <Field label="Goal">
              <div className="grid grid-cols-3 gap-2">
                {["awareness", "community", "hype"].map((g) => (
                  <button key={g} onClick={() => set("goal", g)} data-testid={`goal-${g}`}
                    className={`rounded-lg border px-3 py-2 text-sm capitalize transition-colors ${form.goal === g ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>{g}</button>
                ))}
              </div>
            </Field>
            <Field label="Agent voice for your token">
              <div className="grid grid-cols-3 gap-2">
                {["degen", "clean", "hype"].map((v) => (
                  <button key={v} onClick={() => set("voice", v)} data-testid={`voice-${v}`}
                    className={`rounded-lg border px-3 py-2 text-sm capitalize transition-colors ${form.voice === v ? "border-ai bg-ai/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>{v}</button>
                ))}
              </div>
            </Field>
            <Field label="Desk (optional)">
              <div className="space-y-2">
                <button onClick={() => set("pack", "")} data-testid="pack-none"
                  className={`flex w-full items-center justify-between rounded-xl border p-4 text-left transition-colors ${!form.pack ? "border-juice bg-juice/10" : "border-line bg-raised/30 hover:bg-raised"}`}>
                  <div><p className="font-medium text-cream">No desk — free floor</p><p className="text-xs text-dust">Mint + fee router + 6 AI posts / 24h. No card.</p></div>
                  <span className="font-mono text-lg text-pulp">$0</span>
                </button>
                {packs.map((p) => (
                  <button key={p.id} onClick={() => set("pack", form.pack === p.id ? "" : p.id)} data-testid={`pack-${p.id}`}
                    className={`flex w-full items-center justify-between rounded-xl border p-4 text-left transition-colors ${form.pack === p.id ? "border-juice bg-juice/10" : "border-line bg-raised/30 hover:bg-raised"}`}>
                    <div><p className="font-medium text-cream">{p.desk || p.name} <span className="text-[10px] uppercase tracking-wide text-dust">desk</span></p><p className="text-xs text-dust">{p.window || p.duration} · {p.posts} posts · {(p.agents || []).length} agents</p></div>
                    <span className="font-mono text-lg text-juice">+${p.price}</span>
                  </button>
                ))}
                <p className="text-[11px] text-dust/70">Launch is always free. Desks add posts, agents and campaigns — paid as fuel, never required to start.</p>
              </div>
            </Field>
            <Field label="Creator fee destination">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["wallet", "Wallet", Wallet], ["x", "X handle", Twitter], ["telegram", "Telegram", Send]].map(([t, lbl, Icon]) => (
                  <button key={t} type="button" onClick={() => { setDest({ type: t, verified: t === "wallet", split_pct: t === "wallet" ? 0 : 100 }); setDestCard(null); }} data-testid={`dest-${t}`}
                    className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2.5 text-xs transition-colors ${form.payout_destination.type === t ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>
                    <Icon size={15} /> {lbl}
                  </button>
                ))}
                <button type="button" disabled className="flex cursor-not-allowed flex-col items-center gap-1 rounded-lg border border-line/50 px-3 py-2.5 text-xs text-dust/40" data-testid="dest-split">
                  % Split <span className="text-[9px]">soon</span>
                </button>
              </div>
              {form.payout_destination.type === "wallet" && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-dust"><ShieldCheck size={13} className="text-pulp" /> Fees are converted to USDC and swept to your connected wallet. No 7-day claim window.</p>
              )}
              {form.payout_destination.type !== "wallet" && (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    <Input value={form.payout_destination.ref} onChange={(e) => { setDest({ ref: e.target.value, verified: false }); setDestCard(null); }} placeholder="@handle" className="bg-raised border-line font-mono" data-testid="dest-handle" />
                    <Button type="button" onClick={resolveDest} disabled={resolving} variant="outline" className="shrink-0 border-line bg-raised hover:bg-line/40" data-testid="dest-resolve">
                      {resolving ? <Loader2 size={14} className="animate-spin" /> : "Prove"}
                    </Button>
                  </div>
                  {destCard && (
                    <div className="flex items-center gap-3 rounded-lg border border-line bg-raised/50 p-2.5" data-testid="dest-card">
                      <img src={destCard.avatar} alt="" className="h-8 w-8 rounded-full bg-surface" />
                      <div className="flex-1"><p className="text-sm text-cream">{destCard.name}</p><p className="font-mono text-xs text-dust">{destCard.handle}</p></div>
                      {destCard.verified ? <BadgeCheck size={16} className="text-pulp" /> : <span className="text-xs text-warn">unverified</span>}
                    </div>
                  )}
                  <p className="text-xs leading-relaxed text-dust/80">
                    Fees settle as USDC in the Wen Moon Fee Router. Every creator-fee lot splits <span className="text-cream">70% to you</span> · <span className="text-cream">15% protocol</span> · <span className="text-pulp">15% buy+burn $MOON</span>. Prove this handle + bind a wallet to claim your 70%. Any lot unclaimed after 7 days is bridged to Robinhood Chain and burned to $MOON.
                  </p>
                </div>
              )}
            </Field>
            <div className="rounded-xl border border-pulp/30 bg-pulp/5 p-4">
              <div className="mb-2 flex items-center gap-2 text-pulp"><Flame size={15} /><span className="text-sm font-medium">How the creator fee splits</span></div>
              <div className="grid grid-cols-3 gap-2 font-mono text-sm">
                <div className="rounded-lg bg-surface p-3"><p className="text-[11px] text-dust">You (creator)</p><p className="text-cream">70%</p></div>
                <div className="rounded-lg bg-surface p-3"><p className="text-[11px] text-dust">Protocol</p><p className="text-ai">15%</p></div>
                <div className="rounded-lg bg-surface p-3"><p className="text-[11px] text-dust">Buy+burn $MOON</p><p className="text-pulp">15%</p></div>
              </div>
              <p className="mt-2 text-xs text-dust/70">Off the pad creator fee only, not swap volume. No invoice — you earn when the token trades.</p>
            </div>
          </div>
        )}

        {/* 6 Review */}
        {step === 6 && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Review & launch</h2>
            <div className="flex items-center gap-4 rounded-xl border border-line bg-raised/40 p-4">
              {logoReady && <img src={logoUrl(form.logo)} alt="logo" className="h-14 w-14 rounded-xl border border-line object-cover" data-testid="review-logo" />}
              <div className="flex-1">
                <p className="font-mono text-lg text-cream">${form.ticker}</p>
                <span className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-0.5 text-xs" data-testid="review-dest-chip" style={{ color: DEST_META[form.payout_destination.type]?.color }}>
                  {DEST_META[form.payout_destination.type]?.label}
                  {form.payout_destination.type !== "wallet" && <span className="font-mono text-cream">{form.payout_destination.ref}</span>}
                  {form.payout_destination.type !== "wallet" && <span className="text-dust">· 70% you · 15% protocol · 15% burn</span>}
                </span>
              </div>
            </div>
            <p className="rounded-lg border border-line bg-raised/30 px-3 py-2.5 text-xs leading-relaxed text-dust" data-testid="review-policy">
              Fees are converted to USDC. Wallet payouts send USDC on sweep. X and Telegram payouts are claimable in USDC for 7 days from each lot. Unclaimed lots are bridged to Robinhood Chain and used to buy and burn $MOON.
            </p>
            <div className="space-y-2 rounded-xl border border-line bg-raised/40 p-4 font-mono text-sm">
              {[
                ["Token", `${form.name} ($${form.ticker})`],
                ["Venue", `${currentVenue?.name} · ${currentVenue?.chain}`],
                ["Goal / voice", `${form.goal} / ${form.voice}`],
                ["Socials", Object.values(form.socials).filter(Boolean).join(", ") || "none"],
                ["Desk", currentPack ? `${currentPack.desk || currentPack.name} — +$${currentPack.price}` : "None — free floor (6 posts)"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 border-b border-line/50 pb-2 last:border-0">
                  <span className="text-dust">{k}</span><span className="text-right text-cream">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-dust">Launch is free. Agents launch and post the approved plan. Boosts are optional fuel. Non-refundable once a paid boost starts.</p>
            <Button onClick={submit} disabled={submitting} size="lg" className="w-full gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="wizard-pay-button">
              {submitting ? <><Loader2 size={18} className="animate-spin" /> Fueling up… agents drafting posts</> : (currentPack ? <>Launch + {currentPack.desk || currentPack.name} desk (+${currentPack.price})</> : <>Launch free — start the engine</>)}
            </Button>
          </div>
        )}

        {/* nav */}
        {step < 6 && (
          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={back} disabled={step === 0} className="gap-1 text-dust hover:text-cream" data-testid="wizard-back"><ChevronLeft size={16} /> Back</Button>
            <Button onClick={next} disabled={!canNext()} className="gap-1 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="wizard-next">Continue <ChevronRight size={16} /></Button>
          </div>
        )}
        {step === 6 && (
          <div className="mt-4"><Button variant="ghost" onClick={back} className="gap-1 text-dust hover:text-cream" data-testid="wizard-back"><ChevronLeft size={16} /> Back</Button></div>
        )}
      </div>
    </div>
  );
}
